import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconArmchair2, IconShoppingBag, IconCheck, IconClock, IconPlayerPlay, IconPrinter,
  IconArrowLeft, IconBell, IconSun, IconMoon, IconMaximize,
  IconX, IconPlus, IconAlertTriangle, IconQrcode,
} from '@tabler/icons-react';
import { Badge } from '../components/ui';
import { useTheme } from '../hooks/useTheme';
import CodedPill from '../components/CodedPill';

/**
 * Kitchen Display System (KDS) — clone of DreamsPOS kitchen.html.
 * Three lanes: In Kitchen / Delayed / Completed. Tickets show order#, token,
 * customer, type, items, optional special note, elapsed-time timer, actions.
 *
 * For HL Coffee Shop we keep the 3-lane pattern but the Delayed lane stays
 * mostly empty unless a ticket exceeds 10 min — at which point it auto-shifts
 * lanes and turns the timer red.
 */

type Status = 'in-kitchen' | 'delayed' | 'completed';

interface TicketItem {
  name: string;
  qty: number;
  size?: string;
  /** Default ingredients the customer removed — RED, critical for prep. */
  excludes?: string[];
  /** Add-ons the customer wants on top — GREEN. */
  addons?: string[];
  /** Per-line note from the cashier ("ring + chips, please"). */
  note?: string;
}

interface Ticket {
  id: string;
  orderNo: string;
  token: number;
  customer: string;
  type: 'Dine In' | 'Takeaway' | 'QR';
  table?: string;
  items: TicketItem[];
  /** Order-level note (e.g. customer's note from the QR confirm sheet). */
  note?: string;
  startedAt: number; // epoch ms
  status: Status;
}

const SAMPLE: Ticket[] = [
  {
    id: 't1', orderNo: '#14751', token: 31, customer: 'Sue Allen', type: 'Dine In', table: 'Table 3',
    items: [
      { name: 'Flat White',      qty: 2, size: 'Medium', addons: ['Extra hot', 'Oat milk'] },
      { name: 'Avocado Toastie', qty: 1, excludes: ['Tomato'] },
    ],
    note: 'Extra hot please',
    startedAt: Date.now() - 4 * 60 * 1000, status: 'in-kitchen',
  },
  {
    id: 't2', orderNo: '#14752', token: 32, customer: 'Frank Barrett', type: 'Takeaway',
    items: [
      { name: 'Cappuccino',       qty: 1, size: 'Large' },
      { name: 'Almond Croissant', qty: 2 },
    ],
    startedAt: Date.now() - 2 * 60 * 1000, status: 'in-kitchen',
  },
  {
    id: 't3', orderNo: '#14750', token: 30, customer: 'Walk-in', type: 'Dine In', table: 'Table 7',
    items: [
      { name: 'Chicken Mayo Sub', qty: 1, excludes: ['Onion'], addons: ['Cheese'] },
      { name: 'Iced Latte',       qty: 1, addons: ['Decaf'] },
    ],
    startedAt: Date.now() - 13 * 60 * 1000, status: 'delayed',
  },
  {
    id: 't4', orderNo: '#14749', token: 29, customer: 'Andrew Brooks', type: 'QR', table: 'Table 5',
    items: [
      { name: 'Halloumi Wrap', qty: 1 },
      { name: 'Espresso',      qty: 1 },
    ],
    startedAt: Date.now() - 18 * 60 * 1000, status: 'completed',
  },
];

function formatElapsed(ms: number) {
  const mins = Math.floor(ms / 60000);
  const secs = Math.floor((ms % 60000) / 1000);
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

function TicketCard({ ticket, onMarkDone, onPlay, onPrint }: {
  ticket: Ticket;
  onMarkDone: () => void;
  onPlay: () => void;
  onPrint: () => void;
}) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const elapsed = now - ticket.startedAt;
  const isCompleted = ticket.status === 'completed';
  const isDelayed = ticket.status === 'delayed';

  // Channel chip styling — bold, color-coded, kitchen-scannable.
  const channel =
    ticket.type === 'Dine In'  ? { bg: 'bg-primary', icon: IconArmchair2,    label: 'DINE IN' } :
    ticket.type === 'Takeaway' ? { bg: 'bg-warning', icon: IconShoppingBag,  label: 'TAKEAWAY' } :
                                  { bg: 'bg-purple',  icon: IconQrcode,       label: 'QR' };
  const ChannelIcon = channel.icon;

  const borderTone =
    isDelayed   ? 'border-danger ring-2 ring-danger/30'  :
    isCompleted ? 'border-success' :
                  'border-primary';

  return (
    <article className={[
      'bg-surface rounded-2xl border-2 overflow-hidden flex flex-col shadow-md',
      borderTone,
    ].join(' ')}>
      {/* Header — order + token + timer */}
      <header className={[
        'px-5 py-4 flex items-start justify-between gap-3 border-b-2 border-border',
        isDelayed ? 'bg-danger-soft' : isCompleted ? 'bg-success-soft' : 'bg-light',
      ].join(' ')}>
        <div className="min-w-0">
          {/* Order # + token row — big, bold, the kitchen calls these out */}
          <div className="flex items-baseline gap-3 mb-1.5">
            <span className="text-22 font-bold text-heading tabular-nums leading-none">{ticket.orderNo}</span>
            <span className="inline-flex items-center px-2 h-6 rounded-md bg-heading text-[#FFFFFF] text-13 font-bold tabular-nums tracking-wider">
              TOKEN {String(ticket.token).padStart(3, '0')}
            </span>
          </div>
          <p className="text-16 font-bold text-heading truncate mb-2">{ticket.customer}</p>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1.5 px-2.5 h-7 rounded-md ${channel.bg} text-[#FFFFFF] text-12 font-bold uppercase tracking-wider`}>
              <ChannelIcon size={13} stroke={2.5} />
              {channel.label}
            </span>
            {ticket.table && (
              <span className="inline-flex items-center px-2.5 h-7 rounded-md bg-surface border-2 border-border text-13 font-bold text-heading">
                {ticket.table}
              </span>
            )}
          </div>
        </div>
        {/* Timer — HUGE so the chef sees it from across the room */}
        <div className="flex flex-col items-end flex-shrink-0">
          <IconClock size={14} stroke={2.5} className={isDelayed ? 'text-danger' : isCompleted ? 'text-success' : 'text-gray-700'} />
          <div className={[
            'tabular-nums text-36 font-bold leading-none mt-0.5',
            isDelayed   ? 'text-danger'  :
            isCompleted ? 'text-success' :
                          'text-heading',
          ].join(' ')}>
            {formatElapsed(elapsed)}
          </div>
        </div>
      </header>

      {/* Items — each one its own readable block */}
      <div className="flex-1 px-5 py-4 space-y-3">
        {ticket.items.map((item, i) => {
          const hasMods = (item.excludes?.length ?? 0) + (item.addons?.length ?? 0) > 0;
          return (
            <div
              key={i}
              className={[
                'rounded-lg p-3',
                hasMods ? 'bg-light border border-border-soft' : '',
              ].join(' ')}
            >
              <div className="flex items-center justify-between gap-3 mb-1">
                <p className="text-18 font-bold text-heading leading-tight">{item.name}</p>
                <span className="inline-flex items-center justify-center min-w-[44px] h-10 px-2 rounded-md bg-heading text-[#FFFFFF] text-22 font-bold tabular-nums leading-none">
                  ×{item.qty}
                </span>
              </div>

              {item.size && (
                <span className="inline-flex items-center px-2 h-5 rounded bg-info-soft text-info text-11 font-bold uppercase tracking-wider mb-2">
                  {item.size}
                </span>
              )}

              {/* Exclusions — RED chips. Kitchen-critical. */}
              {item.excludes && item.excludes.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {item.excludes.map((x) => (
                    <span key={x} className="inline-flex items-center gap-1 px-2.5 h-7 rounded-md bg-danger text-[#FFFFFF] text-13 font-bold uppercase tracking-wider shadow-sm">
                      <IconX size={13} stroke={3} />
                      NO {x}
                    </span>
                  ))}
                </div>
              )}

              {/* Add-ons — GREEN chips. */}
              {item.addons && item.addons.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {item.addons.map((a) => (
                    <span key={a} className="inline-flex items-center gap-1 px-2.5 h-7 rounded-md bg-success text-[#FFFFFF] text-13 font-bold uppercase tracking-wider shadow-sm">
                      <IconPlus size={13} stroke={3} />
                      {a}
                    </span>
                  ))}
                </div>
              )}

              {/* Per-line note */}
              {item.note && (
                <p className="mt-2 text-13 italic text-warning bg-warning-soft border-l-4 border-warning px-2 py-1 rounded-r">
                  "{item.note}"
                </p>
              )}
            </div>
          );
        })}

        {/* Order-level note — YELLOW caution banner. Hard to miss. */}
        {ticket.note && (
          <div className="rounded-lg bg-warning text-[#FFFFFF] p-3 flex items-start gap-2 shadow-sm">
            <IconAlertTriangle size={20} stroke={2.5} className="flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-11 font-bold uppercase tracking-widest text-[#FFFFFF]/80 mb-0.5">Customer note</p>
              <p className="text-15 font-bold leading-tight">"{ticket.note}"</p>
            </div>
          </div>
        )}
      </div>

      {/* Actions — BIG. Kitchen hands, not desk hands. */}
      <footer className="px-3 py-3 border-t-2 border-border bg-light flex items-center gap-2">
        {!isCompleted && (
          <>
            <button
              onClick={onPlay}
              className="h-14 px-5 inline-flex items-center justify-center gap-2 rounded-lg border-2 border-border bg-surface text-15 font-bold text-heading hover:border-primary hover:text-primary transition-colors"
              title="Start prep timer"
            >
              <IconPlayerPlay size={16} stroke={2.25} fill="currentColor" />
              Start
            </button>
            <button
              onClick={onMarkDone}
              className="flex-1 h-14 inline-flex items-center justify-center gap-2 rounded-lg bg-success text-[#FFFFFF] text-16 font-bold hover:bg-success-hover transition-colors shadow-sm"
            >
              <IconCheck size={20} stroke={2.5} />
              Mark Ready
            </button>
          </>
        )}
        {isCompleted && (
          <div className="flex-1 h-14 rounded-lg bg-success-soft text-success font-bold text-15 inline-flex items-center justify-center gap-2 uppercase tracking-wider">
            <IconCheck size={18} stroke={3} /> Served
          </div>
        )}
        <button
          onClick={onPrint}
          className="w-14 h-14 inline-flex items-center justify-center rounded-lg border-2 border-border text-gray-700 hover:border-primary hover:text-primary transition-colors"
          title="Print ticket"
        >
          <IconPrinter size={18} stroke={2.25} />
        </button>
      </footer>
    </article>
  );
}

export default function KdsPage() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [tickets, setTickets] = useState<Ticket[]>(SAMPLE);

  const inKitchen = tickets.filter((t) => t.status === 'in-kitchen');
  const delayed   = tickets.filter((t) => t.status === 'delayed');
  const completed = tickets.filter((t) => t.status === 'completed');

  const markDone = (id: string) => setTickets((ts) => ts.map((t) => t.id === id ? { ...t, status: 'completed' } : t));
  const startTimer = (id: string) => setTickets((ts) => ts.map((t) => t.id === id ? { ...t, startedAt: Date.now() } : t));
  const print     = (id: string) => console.log('print', id);

  return (
    <div className="h-screen flex flex-col bg-light overflow-hidden">
      {/* Topbar */}
      <header className="h-topbar bg-surface border-b border-border px-5 flex items-center gap-4 flex-shrink-0">
        <button onClick={() => navigate('/pos')} className="w-9 h-9 inline-flex items-center justify-center rounded-md text-gray-700 hover:bg-gray-100 transition-colors" aria-label="Back">
          <IconArrowLeft size={18} stroke={1.75} />
        </button>
        <button onClick={() => navigate('/pos')} className="flex items-center gap-2.5">
          <img src="/icons/icon-64.png" alt="CODED" className="w-8 h-8 rounded-lg flex-shrink-0 shadow-sm" />
          <span className="flex flex-col leading-tight items-start">
            <span className="text-13 font-bold text-heading">Kitchen Display</span>
            <span className="text-11 text-body">HL Coffee Shop</span>
          </span>
        </button>

        {/* Lane counts */}
        <div className="flex items-center gap-2 ml-6">
          <Badge tone="primary" variant="solid" size="md">
            {String(inKitchen.length).padStart(2, '0')} In Kitchen
          </Badge>
          <Badge tone="danger" variant="solid" size="md">
            {String(delayed.length).padStart(2, '0')} Delayed
          </Badge>
          <Badge tone="success" variant="solid" size="md">
            {String(completed.length).padStart(2, '0')} Completed
          </Badge>
        </div>

        <div className="flex-1" />

        <div className="flex items-center gap-1.5">
          <CodedPill size="xs" />
          <button onClick={toggleTheme} className="w-9 h-9 inline-flex items-center justify-center rounded-md text-gray-700 hover:bg-gray-100 transition-colors">
            {theme === 'dark' ? <IconSun size={18} stroke={1.75} /> : <IconMoon size={18} stroke={1.75} />}
          </button>
          <button className="w-9 h-9 inline-flex items-center justify-center rounded-md text-gray-700 hover:bg-gray-100 transition-colors">
            <IconMaximize size={18} stroke={1.75} />
          </button>
          <button className="relative w-9 h-9 inline-flex items-center justify-center rounded-md text-gray-700 hover:bg-gray-100 transition-colors">
            <IconBell size={18} stroke={1.75} />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-danger rounded-full" />
          </button>
        </div>
      </header>

      {/* Lanes */}
      <div className="flex-1 grid grid-cols-3 gap-4 p-4 min-h-0 overflow-hidden">
        {/* In Kitchen */}
        <section className="flex flex-col min-h-0">
          <div className="flex items-center gap-2 mb-3 flex-shrink-0">
            <IconClock size={18} className="text-primary" />
            <h2 className="text-15 font-bold text-heading">In Kitchen</h2>
            <span className="text-13 text-body tabular-nums">{String(inKitchen.length).padStart(2, '0')}</span>
          </div>
          <div className="flex-1 overflow-y-auto pr-1 space-y-3">
            {inKitchen.map((t) => (
              <TicketCard key={t.id} ticket={t} onMarkDone={() => markDone(t.id)} onPlay={() => startTimer(t.id)} onPrint={() => print(t.id)} />
            ))}
          </div>
        </section>

        {/* Delayed */}
        <section className="flex flex-col min-h-0">
          <div className="flex items-center gap-2 mb-3 flex-shrink-0">
            <IconClock size={18} className="text-danger" />
            <h2 className="text-15 font-bold text-heading">Delayed</h2>
            <span className="text-13 text-body tabular-nums">{String(delayed.length).padStart(2, '0')}</span>
          </div>
          <div className="flex-1 overflow-y-auto pr-1 space-y-3">
            {delayed.map((t) => (
              <TicketCard key={t.id} ticket={t} onMarkDone={() => markDone(t.id)} onPlay={() => startTimer(t.id)} onPrint={() => print(t.id)} />
            ))}
          </div>
        </section>

        {/* Completed */}
        <section className="flex flex-col min-h-0">
          <div className="flex items-center gap-2 mb-3 flex-shrink-0">
            <IconCheck size={18} className="text-success" />
            <h2 className="text-15 font-bold text-heading">Completed</h2>
            <span className="text-13 text-body tabular-nums">{String(completed.length).padStart(2, '0')}</span>
          </div>
          <div className="flex-1 overflow-y-auto pr-1 space-y-3">
            {completed.map((t) => (
              <TicketCard key={t.id} ticket={t} onMarkDone={() => markDone(t.id)} onPlay={() => startTimer(t.id)} onPrint={() => print(t.id)} />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
